import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Divider,
  Chip,
  useTheme,
} from "@mui/material";
import { ExpandMore, HelpOutline, Search } from "@mui/icons-material";

const Help = ({ user }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");

  const faqs = [
    {
      question:
        "Why does the bot say my Steam isn't linked when the website shows it's connected?",
      answer:
        "This usually happens when you're logged into the website with a different Discord account than the one you're using with the bot. This issue is more common than you'd think. Even if you're sure you don't have other discord accounts, please verify you're logged into the correct account by: \n1. Look at the username and display picture in the top-right corner of the website. Make sure they match your actual discord account.\n2. If they do not match, you can click on the display picture on the top right corner to logout, then relog with the correct discord account.\n3. If you keep logging into an incorrect discord account, use an incognito (private) window to login.",
      tags: ["connection", "discord", "pve", "steam", "whitelist"],
    },
    {
      question: 'Why do I get "wrong username or password" after whitelisting?',
      answer:
        "This typically occurs if you missed the final registration step:\n1. After being whitelisted, use the command **`/my_account register`** in Discord, in Viktor's (the bot) direct messages\n2. Double-check your registration with **`/account view`**.",
      tags: ["account", "bot", "pve", "server", "whitelist"],
    },
    {
      question:
        "Why can't the bot access my Steam data even with a public profile?",
      answer:
        "Let's troubleshoot:\n1. Navigate to your steam profile\n2. Click on **Edit Profile** button on the right.\n3. Click on **Privacy Settings** on the left.\n4. Confirm all settings are set to **public**. No exceptions.\n5. If still failing, it's likely you connected an incorrect steam account. In that case, create a support ticket on our discord.",
      tags: ["bot", "privacy", "pve", "steam", "whitelist"],
    },
    // {
    //   question: "I'm getting an 'Unknown Error' during account registration",
    //   answer:
    //     "Don't worry! This rare error usually means:\n1. Someone else has your desired username\n2. There's a temporary system conflict\nPlease try:\n1. A different username\n2. Waiting 10 minutes before retrying\n3. Contact support if it persists",
    //   tags: ["registration", "error"],
    // },
    {
      question:
        "You need to verify your account in order to perform this action.",
      answer:
        "This issue often happens when you're logged into the website using a new or an incorrect discord account.\n1. Make sure you're logged into the correct discord account on the website. Refer to: **Why does the bot say my Steam isn't linked when the website shows it's connected?**\n2. If you're indeed using the correct discord account, please double check your discord account settings and make sure you've successfully verified it.",
      tags: ["verification", "discord"],
    },
  ];

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      )
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto" }}>
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <HelpOutline sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
          Help Center
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
          Find answers to common questions and issues
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Didn't find what you're looking for? Create a support ticket on our{" "}
          <a
            href="https://discord.com/invite/vMRpuuBV7u"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            Discord
          </a>
          .
        </Typography>
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search questions..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: <Search sx={{ color: "text.secondary", mr: 1 }} />,
        }}
        sx={{ mb: 4 }}
      />

      {filteredFaqs.length > 0 ? (
        <Box>
          {filteredFaqs.map((faq, index) => (
            <Accordion
              key={index}
              sx={{
                mb: 2,
                bgcolor: theme.palette.background.paper,
                "&:before": { display: "none" },
                boxShadow: theme.shadows[2],
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" fontWeight={500}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ReactMarkdown>{faq.answer}</ReactMarkdown>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", gap: 1 }}>
                  {faq.tags.map((tag, tagIndex) => (
                    <Chip
                      key={tagIndex}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                      }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <HelpOutline sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No results found for "{searchTerm}"
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Help;
